import { safe } from "@orpc/client";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import z from "zod";
import { useAppForm } from "@/components/form";
import { ComboboxLanguage } from "@/components/forms/combobox-language";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	FieldTitle,
} from "@/components/ui/field";
import {
	FormCard,
	FormCardContent,
	FormCardHeader,
	FormCardTitle,
} from "@/components/ui/form-card";
import { InputGroup, InputGroupAddon, InputGroupText } from "@/components/ui/input-group";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ChannelStatus } from "@/enums/channel-status";
import { EpisodeStatus } from "@/enums/episode-status";
import { EpisodeTypeMeta } from "@/enums/episode-types";
import { useIconUpload } from "@/hooks/use-icon-upload";
import { getCdnUrl } from "@/lib/url";
import { orpc, validationErrors } from "@/orpc/client";

export const Route = createFileRoute("/_app/episodes/$episodeId/")({
	component: Page,
	params: z.object({
		episodeId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.episodes.show.queryOptions({
				input: {
					episodeId: params.episodeId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data: episode } = useSuspenseQuery(
		orpc.episodes.show.queryOptions({
			input: {
				episodeId: params.episodeId,
			},
		}),
	);

	const updateMutation = useMutation(
		orpc.episodes.updateDetails.mutationOptions({
			onSuccess: (_data, _payload, _result, context) => {
				context.client.invalidateQueries({ queryKey: orpc.episodes.key() });
				toast.success("Episodes updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update this episode.");
			},
		}),
	);

	const updateIconMutation = useMutation(
		orpc.episodes.updateIcon.mutationOptions({
			onSuccess: async (_data, _payload, _result, context) => {
				await context.client.invalidateQueries({ queryKey: orpc.episodes.key() });
				await context.client.invalidateQueries({
					queryKey: orpc.episodes.show.key({
						input: { episodeId: params.episodeId },
					}),
				});
				toast.success("Episode icon updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update the episode icon.");
			},
		}),
	);

	const uploadMutation = useMutation(
		orpc.episodes.uploadIcon.mutationOptions({
			onError: () => {
				toast.error("Failed to start the icon upload.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			SeasonNum: episode.SeasonNum || null,
			EpisodeNum: episode.EpisodeNum,
			Title: episode.Title || "",
			Description: episode.Description || "",
			HomepageUrl: episode.HomepageUrl || "",
			Icon: null as File | null,
			LanguageCode: episode.LanguageCode,
			TypeID: episode.TypeID,
			IsExplicit: episode.IsExplicit,
			PublishDate: episode.PublishDate || null,
			ExpiryDate: episode.ExpiryDate || null,
			Summary: episode.Summary || "",
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmitAsync: async ({ value }) => {
				const { Icon: _icon, ...episodeDetails } = value;
				const { error } = await safe(
					updateMutation.mutateAsync({
						...episodeDetails,
						ID: episode.ID,
					}),
				);

				if (error) {
					return validationErrors(error) ?? "An error occurred while updating the episode.";
				}
			},
		},
	});

	const {
		iconInputRef,
		iconPreviewUrl,
		iconUploadError,
		iconUploadProgress,
		isIconUploading,
		uploadIconFile,
		removeIcon,
		setIconUploadError,
		setIconUploadProgress,
	} = useIconUpload({
		selectedFile: form.state.values.Icon,
		requestUploadTarget: async (contentType) => {
			const { data: uploadData, error: uploadError } = await safe(
				uploadMutation.mutateAsync({
					ContentType: contentType,
					ID: episode.ID,
				}),
			);

			if (uploadError || !uploadData) {
				return null;
			}

			return uploadData;
		},
		persistIcon: async (iconKey) => {
			const { error: updateIconError } = await safe(
				updateIconMutation.mutateAsync({
					ID: episode.ID,
					IconKey: iconKey,
				}),
			);

			return !updateIconError;
		},
		clearSelectedFile: () => {
			form.setFieldValue("Icon", null);
		},
	});

	const currentIconUrl = iconPreviewUrl ?? getCdnUrl(episode.Icon?.ThumbnailURL);
	const isIconBusy = isIconUploading || uploadMutation.isPending || updateIconMutation.isPending;
	const isEpisodeEditable =
		![EpisodeStatus.Removed].includes(episode.StatusID) &&
		![ChannelStatus.Removed, ChannelStatus.Removed_TS].includes(episode.Channel.StatusID);

	return (
		<form.AppForm>
			<form
				className="relative"
				id={form.formId}
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<div className="grid grid-cols-12 gap-6 px-4 lg:px-6">
					<FormCard
						className="col-span-12"
						disabled={!isEpisodeEditable || updateMutation.isPending || isIconBusy}
					>
						<FormCardHeader>
							<FormCardTitle>Episode Info</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="Icon"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-episode-icon`}
											className="col-span-12"
										>
											<field.FormLabel>Episode Icon</field.FormLabel>
											<div className="flex flex-col gap-4 rounded-lg border p-4 sm:flex-row sm:items-center">
												<Avatar className="size-20" rounded="sm">
													<AvatarImage src={currentIconUrl} alt={episode.Title ?? "Episode icon"} />
													<AvatarFallback className="text-lg">
														{episode.Title?.slice(0, 1).toUpperCase() ?? "E"}
													</AvatarFallback>
												</Avatar>
												<div className="flex flex-1 flex-col gap-2">
													<div>
														<p className="text-sm font-medium">
															{field.state.value?.name ?? "Upload a JPG or PNG icon"}
														</p>
														<field.FormDescription>
															Recommended: 3000x3000 px square image, JPG or PNG.
														</field.FormDescription>
													</div>
													<div className="flex flex-wrap items-center gap-3">
														<Button
															type="button"
															variant="outline"
															onClick={() => iconInputRef.current?.click()}
															disabled={isIconBusy}
														>
															{isIconBusy ? "Uploading..." : "Choose icon"}
														</Button>
														{episode.IconID && (
															<Button
																type="button"
																variant="destructive"
																onClick={() => {
																	void removeIcon();
																}}
																disabled={isIconBusy}
															>
																Remove Icon
															</Button>
														)}
														<input
															ref={iconInputRef}
															id={`${field.form.formId}-episode-icon`}
															type="file"
															accept="image/jpeg,image/jpg,image/png"
															className="hidden"
															disabled={isIconBusy}
															onBlur={field.handleBlur}
															onChange={(event) => {
																const nextFile = event.target.files?.[0] ?? null;

																field.handleChange(nextFile);
																setIconUploadError(null);
																setIconUploadProgress(null);

																if (nextFile) {
																	void uploadIconFile(nextFile);
																}
															}}
														/>
														{iconUploadProgress !== null && (
															<span className="text-muted-foreground text-sm">
																{iconUploadProgress}% uploaded
															</span>
														)}
													</div>
													{iconUploadProgress !== null && (
														<div className="bg-muted h-2 overflow-hidden rounded-full">
															<div
																className="bg-primary h-full transition-[width]"
																style={{ width: `${iconUploadProgress}%` }}
															/>
														</div>
													)}
													{iconUploadError && (
														<p className="text-destructive text-sm" role="alert">
															{iconUploadError}
														</p>
													)}
												</div>
											</div>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Title"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Title</field.FormLabel>
											<InputGroup>
												<field.FormGroupInput />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/500 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Summary"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Summary</field.FormLabel>
											<InputGroup>
												<field.FormTextEditor className="max-h-50 border-0 shadow-none" />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/4000 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Description"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Description</field.FormLabel>
											<InputGroup>
												<field.FormTextEditor className="border-0 shadow-none" />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/4000 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="HomepageUrl"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Homepage URL</field.FormLabel>
											<field.FormInput type="url" />
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard
						className="col-span-12"
						disabled={!isEpisodeEditable || updateMutation.isPending}
					>
						<FormCardHeader>
							<FormCardTitle>Episode Content</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="LanguageCode"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-episode-language`}
											className="col-span-6"
										>
											<field.FormLabel>Language</field.FormLabel>
											<ComboboxLanguage
												id={`${field.form.formId}-episode-language`}
												value={field.state.value}
												onChange={(value) => field.handleChange(value)}
												onBlur={field.handleBlur}
												isInvalid={field.state.meta.isTouched && !field.state.meta.isValid}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="IsExplicit"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-episode-explicit`}
											className="col-span-6"
										>
											<field.FormLabel>Explicit Content</field.FormLabel>
											<RadioGroup
												className="flex flex-row gap-2"
												value={field.state.value}
												onValueChange={field.handleChange}
												onBlur={field.handleBlur}
											>
												<FieldLabel>
													<Field orientation="horizontal" className="h-8 py-1.5!">
														<FieldContent>
															<FieldTitle>Yes</FieldTitle>
														</FieldContent>
														<RadioGroupItem value={true} />
													</Field>
												</FieldLabel>
												<FieldLabel>
													<Field orientation="horizontal" className="h-8 py-1.5!">
														<FieldContent>
															<FieldTitle>No</FieldTitle>
														</FieldContent>
														<RadioGroupItem value={false} />
													</Field>
												</FieldLabel>
											</RadioGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard
						className="col-span-12"
						disabled={!isEpisodeEditable || updateMutation.isPending}
					>
						<FormCardHeader>
							<FormCardTitle>Episode Publication</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="PublishDate"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-episode-publish-date`}
											className="col-span-6"
										>
											<field.FormLabel>Publish Date</field.FormLabel>
											<field.FormDateTimePicker />
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="ExpiryDate"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-episode-expiry-date`}
											className="col-span-6"
										>
											<field.FormLabel>Expiration Date</field.FormLabel>
											<field.FormDateTimePicker />
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard
						className="col-span-12"
						disabled={!isEpisodeEditable || updateMutation.isPending}
					>
						<FormCardHeader>
							<FormCardTitle>Episode Type</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup className="col-span-12">
								<form.AppField
									name="TypeID"
									children={(field) => (
										<RadioGroup
											value={field.state.value}
											onValueChange={field.handleChange}
											onBlur={field.handleBlur}
											className="col-span-full"
										>
											{Object.values(EpisodeTypeMeta).map((episodeType) => (
												<FieldLabel key={episodeType.id} htmlFor={`type-${episodeType.id}`}>
													<Field orientation="horizontal">
														<FieldContent>
															<FieldTitle>{episodeType.label}</FieldTitle>
															<FieldDescription className="text-xs">
																{episodeType.description}
															</FieldDescription>
														</FieldContent>
														<RadioGroupItem value={episodeType.id} id={`type-${episodeType.id}`} />
													</Field>
												</FieldLabel>
											))}
										</RadioGroup>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					{isEpisodeEditable && <form.FormSaveBar />}
				</div>
				{isEpisodeEditable && <form.FormBottomBar />}
			</form>
		</form.AppForm>
	);
}
